import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Link2, Loader2, ShieldCheck, Unlink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { venueTradingService, type Venue } from '@/services/venueTradingService';
import T from "@/i18n/T";

const VENUES = [
  {
    id: 'kalshi' as const,
    name: 'Kalshi',
    blurb:
      'US-regulated exchange. Requires a funded Kalshi account plus a personal API key ID and RSA private key generated in your Kalshi settings.',
    eligibility: 'US residents only; some states are excluded by the exchange.',
  },
  {
    id: 'polymarket' as const,
    name: 'Polymarket',
    blurb:
      'On-chain CLOB on Polygon. Requires a Polygon wallet funded with USDC and exchange allowances approved.',
    eligibility: 'Not available to US persons.',
  },
];

export default function VenueAccountPanel() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<Venue | null>(null);
  const [kalshiKeyId, setKalshiKeyId] = useState('');
  const [kalshiPem, setKalshiPem] = useState('');
  const [polyKey, setPolyKey] = useState('');

  const { data: connections = [], isLoading } = useQuery({
    queryKey: ['venue-connections', user?.id],
    queryFn: () => venueTradingService.listConnections(),
    enabled: !!user,
  });

  const connected = (v: Venue) => connections.some((c) => c.venue === v);

  const reset = () => {
    setKalshiKeyId('');
    setKalshiPem('');
    setPolyKey('');
    setDialog(null);
  };

  const connectMutation = useMutation({
    mutationFn: async (venue: Venue) => {
      if (venue === 'kalshi') {
        return venueTradingService.connectKalshi(kalshiKeyId.trim(), kalshiPem.trim());
      }
      return venueTradingService.connectPolymarket(polyKey.trim());
    },
    onSuccess: (_d, venue) => {
      toast.success(`${venue === 'kalshi' ? 'Kalshi' : 'Polymarket'} connected`);
      qc.invalidateQueries({ queryKey: ['venue-connections'] });
      reset();
    },
    onError: (e: Error) => toast.error('Could not connect', { description: e.message }),
  });

  const disconnectMutation = useMutation({
    mutationFn: (venue: Venue) => venueTradingService.disconnect(venue),
    onSuccess: () => {
      toast.success('Account disconnected');
      qc.invalidateQueries({ queryKey: ['venue-connections'] });
    },
    onError: (e: Error) => toast.error('Could not disconnect', { description: e.message }),
  });

  return (
    <div className="space-y-4">
      <Card className="border-amber-500/40 bg-amber-500/5">
        <CardContent className="pt-6 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="text-sm space-y-1">
            <p className="font-semibold"><T k="auto.trading-with-real-money-carries-real-ris-acf0" /></p>
            <p className="text-muted-foreground">
              Byteline shows market data and routes orders to the venue you connect. Positions can
              expire worthless. You are responsible for your own eligibility, taxes, and the terms
              of each exchange.
            </p>
          </div>
        </CardContent>
      </Card>

      {!user && (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            <T k="auto.sign-in-to-connect-a-trading-account-75f2" />
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {VENUES.map((v) => {
          const isOn = connected(v.id);
          return (
            <Card key={v.id}>
              <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
                <CardTitle className="text-base">{v.name}</CardTitle>
                <Badge variant={isOn ? 'default' : 'outline'}>
                  {isLoading ? 'Checking…' : isOn ? 'Connected' : 'Not connected'}
                </Badge>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p className="text-muted-foreground">{v.blurb}</p>
                <p className="text-xs text-muted-foreground">
                  <span className="font-semibold"><T k="auto.eligibility-fb28" /></span> {v.eligibility}
                </p>
                {isOn ? (
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={disconnectMutation.isPending}
                    onClick={() => disconnectMutation.mutate(v.id)}
                  >
                    <Unlink className="h-4 w-4 mr-1.5" /> Disconnect {v.name}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={!user}
                    onClick={() => setDialog(v.id)}
                  >
                    <Link2 className="h-4 w-4 mr-1.5" /> Connect {v.name}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center gap-2 space-y-0">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <CardTitle className="text-base"><T k="auto.how-your-credentials-are-handled-e38d" /></CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            Venue keys are encrypted at rest and only ever decrypted inside backend functions, they
            are never sent back to the browser and never stored in this app's frontend.
          </p>
          <p>
            Every order is signed server-side after your session is verified, re-checked against the
            live market, and capped to whole shares at a 1–99&cent; limit price.
          </p>
          <p><T k="auto.disconnecting-a-venue-deletes-the-stored-3f3f" /></p>
        </CardContent>
      </Card>

      {/* Kalshi dialog */}
      <Dialog open={dialog === 'kalshi'} onOpenChange={(o) => !o && reset()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><T k="auto.connect-kalshi-d258" /></DialogTitle>
            <DialogDescription>
              Create an API key in Kalshi → Account → API Keys. Paste the key ID and the RSA private
              key you downloaded.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="kalshi-key"><T k="auto.api-key-id-6452" /></Label>
              <Input
                id="kalshi-key"
                value={kalshiKeyId}
                onChange={(e) => setKalshiKeyId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="kalshi-pem"><T k="auto.rsa-private-key-pem-b5cb" /></Label>
              <Textarea
                id="kalshi-pem"
                rows={6}
                value={kalshiPem}
                onChange={(e) => setKalshiPem(e.target.value)}
                placeholder={'-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----'}
                className="font-mono text-xs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={reset}>
              <T k="auto.cancel-ea47" />
            </Button>
            <Button
              disabled={!kalshiKeyId.trim() || !kalshiPem.trim() || connectMutation.isPending}
              onClick={() => connectMutation.mutate('kalshi')}
            >
              {connectMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Polymarket dialog */}
      <Dialog open={dialog === 'polymarket'} onOpenChange={(o) => !o && reset()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><T k="auto.connect-polymarket-d376" /></DialogTitle>
            <DialogDescription>
              Paste the private key of the Polygon wallet you trade with. Use a dedicated trading
              wallet holding only what you intend to trade, the key signs your CLOB orders.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="poly-key"><T k="auto.polygon-private-key-afbf" /></Label>
            <Input
              id="poly-key"
              type="password"
              value={polyKey}
              onChange={(e) => setPolyKey(e.target.value)}
              placeholder="0x…"
              className="font-mono text-xs"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={reset}>
              <T k="auto.cancel-ea47" />
            </Button>
            <Button
              disabled={!polyKey.trim() || connectMutation.isPending}
              onClick={() => connectMutation.mutate('polymarket')}
            >
              {connectMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
